import { AppInitHelper } from '../../services/helpers/app-init.helper';
import { WorkspaceStore } from '../../services/stores/workspace.store';
import { Component, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener, OnInit } from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { DashboardHelper } from '../../services/dashboard.service';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { BaseComponent } from '../base/base.component';
import { IWorkspaceInstanceModel } from '../../models/workspace/workspace-instance.model';
import { UserService } from '../../services/user.service';
import { Helper } from '../../services/helper.service';
import { BlueprintStore } from '../../services/stores/blueprint.store';
import { TreeNode } from 'primeng/api';
@Component({
  selector: 'app-catch-hub-dashboard',
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [style({ transform: 'translateX(-100%)', opacity: 0 }), animate('450ms', style({ transform: 'translateX(0)', opacity: 1 }))]),
      transition(':leave', [style({ transform: 'translateX(0)', opacity: 1 }), animate('450ms', style({ transform: 'translateX(-100%)', opacity: 0 }))]),
    ]),
  ],
  templateUrl: './catch-hub-dashboard.component.html',
  styleUrls: ['./catch-hub-dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CatchHubDashboardComponent extends BaseComponent implements AfterViewInit, OnInit {
  private className = 'CatchHubDashboardComponent';
  public isServerProcessing = false;
  public isViewLoading = false;

  readonly rightPanelDefaultWidth = 450;
  readonly rightPanelMinWidth = 420;
  rightPanelMaxWidth = this.dashboardHelper.getRightPanelMaxWidth(this.rightPanelMinWidth);
  rightPanelWidth = this.rightPanelDefaultWidth;
  private isRightPanelResizing = false;
  private resizeStartX = 0;
  private resizeStartWidth = this.rightPanelDefaultWidth;
  private isRightPanelExpanded = false;
  private lastShowRightPanelState = false;
  private lastRightPanelNodeId: string | null = null;

  selectedWorkspace: IWorkspaceInstanceModel = {} as IWorkspaceInstanceModel;
  workspaceNodes: Array<any> = [];
  blueprint: any;

  private rightPanelVisibleBeforeNav = false;

  assignedToFieldId: string | undefined = '';
  testStepsFieldId: string | undefined = '';
  public sourceFieldId: string | undefined = '';
  inFocus = false;
  isAttachmentOpen = false;

  selectedSettingsNode: TreeNode | null = null;
  private settingsTreeNodesCache: TreeNode[] = [];

  constructor(
    public dashboardHelper: DashboardHelper,
    private userService: UserService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private helperService: Helper,
    private workspaceStore: WorkspaceStore,
    private appinitService: AppInitHelper,
    private blueprintStore: BlueprintStore,
  ) {
    super();
    this.dashboardHelper.setShowRightPanel(false);
    this.dashboardHelper.showVideoList = false;
  }

  // The blueprint editor's panels have no header of their own, so the shared one must stay visible when expanded to offer the collapse button.
  public readonly isBlueprintEditorActive$ = this.blueprintStore.$blueprintEditorActive;

  ngOnInit(): void {
    this.appinitService.initApplication();
    this.appinitService.loadInitialData();

    this.subscribers.settingsTreeNodes = this.dashboardHelper.settingsTreeNodes.subscribe({
      next: (nodes) => {
        this.settingsTreeNodesCache = nodes ?? [];
        this.syncSelectedSettingsNode();
      },
      error: (err: any) => console.log(err),
    });

    // The panel's own close button fires this. A navigation already in flight when it does (selecting
    // the node routes the right-panel outlet) captured "visible" at NavigationStart, and would put the
    // panel back at NavigationEnd with no node behind it - an empty edit panel. An explicit close
    // overrides that snapshot; the next NavigationStart takes a fresh one.
    this.subscribers.nodeSelectionCleared = this.workspaceStore.$nodeSelectionCleared.subscribe(() => {
      this.rightPanelVisibleBeforeNav = false;
    });

    this.subscribers.routerEvents = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.rightPanelVisibleBeforeNav = this.dashboardHelper.getShowRightPanel();
        if (!event.url.startsWith('/dashboard')) {
          this.resetRightPanelWidthState();
        }
        const currentMain = this.getMainPanelRoute(this.router.url);
        const targetMain = this.getMainPanelRoute(event.url);
        if (targetMain && targetMain !== currentMain) {
          this.isViewLoading = true;
          this.cdr.detectChanges();
        }
      }

      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        const url = event instanceof NavigationEnd ? event.urlAfterRedirects : (event as any).url ?? '';
        const isReportView = url.includes('mainPanel:smartreport') || url.includes('mainPanel%3Asmartreport');
        const isEditPanel = url.includes('rightPanel:shared-edit') || url.includes('rightPanel%3Ashared-edit');
        // The right-panel body is only ever a rightPanel router-outlet, so it must stay
        // hidden when the destination routes nothing into that outlet. Otherwise navigating
        // back to a list view (e.g. Projects) restores the pre-nav visible state onto an
        // empty outlet and shows a blank panel.
        const hasRightPanelOutlet = url.includes('rightPanel:') || url.includes('rightPanel%3A');
        const restored = isReportView && isEditPanel ? false : this.rightPanelVisibleBeforeNav;
        this.dashboardHelper.setShowRightPanel(hasRightPanelOutlet ? restored : false);
        if (this.isViewLoading) {
          this.isViewLoading = false;
          this.cdr.detectChanges();
        }
        this.syncSelectedSettingsNode();
      }
    });
  }

  private getMainPanelRoute(url: string): string {
    const match = url.match(/mainPanel[:%3A]+(\w[\w-]*)/);
    return match ? match[1] : '';
  }

  // Keeps the settings/admin tree's highlighted node in sync with whichever admin
  // tab is actually active in the tabPanel outlet — covers direct navigation, page
  // refresh, and browser back/forward, not just clicks (those are already reflected
  // via the p-tree [(selection)] binding).
  private syncSelectedSettingsNode(): void {
    const activeTab = this.getActiveSettingsTab(this.router.url);
    if (!activeTab) {
      return;
    }

    const match = this.findSettingsNodeByTab(this.settingsTreeNodesCache, activeTab);
    if (match && match !== this.selectedSettingsNode) {
      this.selectedSettingsNode = match;
      this.cdr.detectChanges();
    }
  }

  private getActiveSettingsTab(url: string): string {
    if (!url) {
      return '';
    }
    const match = url.match(/tabPanel(?:%3A|:)admin(?:%2F|\/)([\w-]+)/);
    return match ? match[1] : '';
  }

  private findSettingsNodeByTab(nodes: TreeNode[] | null | undefined, tab: string): TreeNode | null {
    if (!nodes?.length) {
      return null;
    }
    for (const node of nodes) {
      if (node.data?.link?.[2]?.outlets?.tabPanel?.[1] === tab) {
        return node;
      }
      if (node.children?.length) {
        const childMatch = this.findSettingsNodeByTab(node.children, tab);
        if (childMatch) {
          return childMatch;
        }
      }
    }
    return null;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    if (this.subscribers) {
      Object.keys(this.subscribers).forEach((key) => {
        if (this.subscribers[key]) {
          this.subscribers[key].unsubscribe();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.initializeRightPanelUiState();

    setTimeout(() => {
      this.subscribers.permission = this.userService.permission.subscribe((p) => {
        if (p) {
          console.log('[Permissions] Dashboard load - effective permissions (union of groups/roles):', {
            userId: p.userId,
            groups: p.roles,
            permissions: p.permissions,
            projectPermissions: p.projectPermissions,
          });
        }
      });

      this.subscribers.isServerProcessing = this.helperService.isServerProcessing.subscribe((resp) => {
        this.isServerProcessing = resp;
        this.cdr.detectChanges(); // force UI update
      });

      this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((data) => {
        if (data?.bluePrintId) {
          this.blueprintStore.ensureBlueprintLoaded(data.bluePrintId);
        }

        this.initializeWorkspaceData(data);
      });

      this.subscribers.blueprintLoaded = this.blueprintStore.$selectedBluePrint.subscribe((blueprint) => {
        const workspace = this.workspaceStore.selectedWorkspaceInstance;
        if (!workspace?.bluePrintId || !blueprint || blueprint.id !== workspace.bluePrintId) {
          return;
        }

        this.initializeWorkspaceData(workspace);
      });

      this.subscribers.allBlueprints = this.blueprintStore.$allBluePrints.subscribe(() => {
        const workspace = this.workspaceStore.selectedWorkspaceInstance;
        if (!workspace?.bluePrintId) {
          return;
        }

        this.initializeWorkspaceData(workspace);
      });

      this.subscribers.patchedEdgeInstance = this.workspaceStore.$patchedEdgeInstance.subscribe((res: any) => {
        if (res) {
          this.selectedWorkspace = this.helperService.getDeepCopy(this.selectedWorkspace);
          if (this.selectedWorkspace.edgeInstances) {
            if (this.selectedWorkspace.edgeInstances.findIndex((ni) => ni?.id === res.id) > -1) {
              this.selectedWorkspace.edgeInstances[this.selectedWorkspace.edgeInstances.findIndex((ni) => ni?.id === res.id)] = res;
            } else {
              this.selectedWorkspace.edgeInstances.push(res);
            }
          } else {
            this.selectedWorkspace.edgeInstances = [];
            this.selectedWorkspace.edgeInstances.push(res);
          }
          this.loadNodesForWorkspace();
        }
      });

      this.cdr.detectChanges();
    });
  }

  private initializeWorkspaceData(workspace: IWorkspaceInstanceModel | null | undefined): void {
    if (!workspace) {
      return;
    }

    const blueprint = this.blueprintStore.getBlueprintByIdFromCache(workspace.bluePrintId);
    if (!this.blueprintStore.isBlueprintHydrated(blueprint)) {
      this.blueprintStore.ensureBlueprintLoaded(workspace.bluePrintId);
      return;
    }

    this.selectedWorkspace = workspace;
    this.assignedToFieldId = blueprint.fields.find((bf) => bf.name.toLowerCase() === 'assignee')?.id || '';
    this.loadNodesForWorkspace();
    this.cdr.detectChanges();
  }

  loadNodesForWorkspace() {
    if (this.selectedWorkspace) {
      this.workspaceNodes = this.selectedWorkspace.nodeInstances;
      this.blueprint = this.blueprintStore.getBlueprintByIdFromCache(this.selectedWorkspace.bluePrintId);
    }
  }

  onSettingsNodeSelect(node: TreeNode) {
    if (node?.data?.link) {
      this.router.navigate(node.data.link);
    }
  }

  onCloseShortRightPanel() {
    this.dashboardHelper.setShowRightPanel(false);
    this.dashboardHelper.setExpandRightPanel(false);
    this.dashboardHelper.showVideoList = false;
    this.dashboardHelper.showProfile = false;
    this.rightPanelWidth = this.rightPanelDefaultWidth;
    this.dashboardHelper.setRightPanelWidthPx(null);
  }

  onExpandShortRightPanel(isexpand: boolean) {
    this.dashboardHelper.setExpandRightPanel(isexpand);
  }

  onRightPanelResizeStart(event: MouseEvent) {
    this.isRightPanelResizing = true;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.rightPanelWidth;
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('window:mousemove', ['$event'])
  onRightPanelResizeMove(event: MouseEvent) {
    if (!this.isRightPanelResizing) {
      return;
    }
    const deltaX = this.resizeStartX - event.clientX;
    const newWidth = this.resizeStartWidth + deltaX;
    this.applyRightPanelWidth(newWidth, true);
  }

  @HostListener('window:mouseup')
  onRightPanelResizeEnd() {
    if (this.isRightPanelResizing) {
      this.isRightPanelResizing = false;
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.rightPanelMaxWidth = this.dashboardHelper.getRightPanelMaxWidth(this.rightPanelMinWidth);
    if (this.rightPanelWidth > this.rightPanelMaxWidth) {
      this.applyRightPanelWidth(this.rightPanelMaxWidth);
    }
  }

  private applyRightPanelWidth(width: number, persist = false) {
    if (!Number.isFinite(width)) {
      return;
    }
    this.rightPanelMaxWidth = this.dashboardHelper.getRightPanelMaxWidth(this.rightPanelMinWidth);
    const clampedWidth = this.dashboardHelper.clampRightPanelWidth(width, this.rightPanelMinWidth);
    this.rightPanelWidth = clampedWidth;
    if (persist) {
      this.dashboardHelper.setRightPanelWidthPx(clampedWidth);
    }
    this.cdr.markForCheck();
  }

  private initializeRightPanelUiState() {
    this.applyRightPanelWidth(this.rightPanelDefaultWidth);

    this.subscribers.showRightPanelChange = this.dashboardHelper.$showRightPanel.subscribe((isVisible) => {
      // While the panel is in expanded mode, keep its width pinned to maxAllowed.
      // Without this, a setShowRightPanel(true) re-emission from a save callback
      // (e.g., after the user changes status via the header dropdown) takes the
      // else-branch and resets rightPanelWidth to the stored or default value,
      // visually narrowing the panel even though isRightPanelExpanded stays true.
      if (this.isRightPanelExpanded) {
        const maxAllowed = this.dashboardHelper.getRightPanelMaxWidth(this.rightPanelMinWidth);
        this.applyRightPanelWidth(maxAllowed);
        this.lastShowRightPanelState = isVisible;
        return;
      }
      if (isVisible && !this.lastShowRightPanelState) {
        const target = this.dashboardHelper.getRightPanelWidthPx() ?? this.rightPanelDefaultWidth;
        this.applyRightPanelWidth(target);
      } else {
        this.isRightPanelResizing = false;
        this.rightPanelWidth = this.dashboardHelper.getRightPanelWidthPx() ?? this.rightPanelDefaultWidth;
      }
      this.lastShowRightPanelState = isVisible;
    });

    this.subscribers.rightPanelExpanded = this.dashboardHelper.isRightPanelExpanded.subscribe((expanded) => {
      this.isRightPanelExpanded = expanded;
      const maxAllowed = this.dashboardHelper.getRightPanelMaxWidth(this.rightPanelMinWidth);
      if (expanded) {
       
        if (this.rightPanelWidth > 0 && this.rightPanelWidth < maxAllowed) {
          this.dashboardHelper.setRightPanelWidthPx(this.rightPanelWidth);
        }
        this.applyRightPanelWidth(maxAllowed);
      } else {
        const stored = this.dashboardHelper.getRightPanelWidthPx();
        const target = stored ?? this.rightPanelDefaultWidth;
        this.applyRightPanelWidth(Math.min(target, maxAllowed));
      }
    });
  }

  private resetRightPanelWidthState(): void {
    this.dashboardHelper.setRightPanelWidthPx(null);
    this.lastRightPanelNodeId = null;
  }
}
